'use client'

import { Link, useLocation } from 'react-router'
import { useEffect, useMemo, useState } from 'react'
import { Home, CreditCard, FileText, User, ChevronDown } from "lucide-react"
import { useAuth } from '@/components/auth-provider'
import { isBusinessAccountsPath, isApplyFlowPath } from '@/lib/business-app-path'

const staticNavItems = [
  { href: '/', label: 'Home', icon: Home },
  { href: '/accounts', label: 'Accounts', icon: CreditCard },
  { href: '/apply', label: 'Apply', icon: FileText },
]

export default function MobileNav() {
  const { pathname } = useLocation()
  const { status } = useAuth()
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Omit the auth-specific 4th tab entirely while auth is still resolving to
  // prevent a brief "Sign in" flash for already-signed-in users.
  // Once status resolves: show "Account" for authenticated users, "Sign in" for
  // everyone else. Avoids advertising a link that ProtectedRoute would bounce.
  const navItems = useMemo(() => {
    if (status === 'loading') { return staticNavItems }
    const isAuthed = status === 'authenticated'
    return [
      ...staticNavItems,
      isAuthed
        ? { href: '/account/portal', label: 'Account', icon: User }
        : { href: '/login', label: 'Sign in', icon: User },
    ]
  }, [status])

  useEffect(() => {
    if (!pathname) {
      setIsCollapsed(false)
      return
    }
    if (isApplyFlowPath(pathname)) {
      setIsCollapsed(true)
    } else {
      setIsCollapsed(false)
    }
  }, [pathname])

  if (!pathname) {return null}
  if (pathname.startsWith('/embedded-persona')) {return null}
  if (isApplyFlowPath(pathname)) {return null}
  if (pathname.startsWith('/approval')) {return null}
  if (pathname === '/accounts' || isBusinessAccountsPath(pathname)) {
    return null
  }

  return (
    <nav className="md:hidden fixed inset-x-0 bottom-0 z-50 pointer-events-none">
      <div className="pointer-events-auto mx-auto w-full max-w-md px-4 pb-[calc(env(safe-area-inset-bottom,0.75rem)+0.75rem)]">
        <div className="flex flex-col items-center gap-2">
          <button
            type="button"
            aria-expanded={!isCollapsed}
            className="inline-flex items-center gap-2 border border-cfb-shadow bg-cfb-light/95 px-4 py-1.5 text-xs font-semibold text-gray-700 shadow-[0_4px_16px_rgba(15,23,42,0.12)] backdrop-blur transition hover:bg-cfb-light"
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {isCollapsed ? 'Open navigation' : 'Hide navigation'}
            {isCollapsed ? <ChevronDown className="h-4 w-4 rotate-180" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {!isCollapsed && (
            <div className="w-full rounded-full border border-cfb-shadow bg-cfb-light/95 backdrop-blur shadow-[0_8px_30px_rgba(15,23,42,0.15)]">
              <ul className="flex justify-between items-center px-4 py-2 text-xs font-medium text-gray-600">
                {navItems.map(({ href, label, icon: Icon }) => {
                  const isActive =
                    href === '/'
                      ? pathname === '/'
                      : pathname.startsWith(href)

                  return (
                    <li key={href}>
                      <Link
                        to={href}
                        className={`flex flex-col items-center gap-1 px-3 py-1.5 transition-all duration-150 ${
                          isActive
                            ? 'text-brand bg-brand-soft'
                            : 'text-gray-500 hover:text-brand'
                        }`}
                      >
                        <Icon className={`h-5 w-5 ${isActive ? 'text-brand' : 'text-gray-400'}`} />
                        <span>{label}</span>
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </nav>
  )
}

